import { useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import ReactDataTable from 'react-data-table-component';
import {
    IconEdit, IconEye, IconTrash, IconPlus, IconRefresh, IconSend,
    IconUpload, IconX, IconFileText, IconPhoto, IconCheck,
} from '@tabler/icons-react';
import toast from 'react-hot-toast';
import Breadcrumb from '../../components/common/Breadcrumb';
import GrantProposalTab from '../../components/common/GrantProposalTab';
import ActionDropdown from '../../components/common/ActionDropdown';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { TableLoader } from '../../components/common/Loader';
import { useAuth } from '../../contexts/AuthContext';
import {
    useGrantDrafts,
    useGrantInternalReview,
    useGrantSubmitted,
    useGrantAwarded,
    useGrantRejected,
    useDeleteGrantProposal,
    useTransitionGrantProposal,
    useUploadGrantAttachments,
} from '../../hooks/useGrants';

// ── Upload modal ──────────────────────────────────────────────────────────

function UploadDocumentsModal({ proposal, onClose }) {
    const { user } = useAuth();
    const orgId = user?.orgId;
    const fileInputRef = useRef(null);
    const [documents, setDocuments] = useState([]);
    const uploadAttachments = useUploadGrantAttachments();

    const handleFileChange = (e) => {
        const mapped = Array.from(e.target.files).map(file => ({
            file,
            name: file.name,
            type: file.type,
        }));
        setDocuments(prev => [...prev, ...mapped]);
        e.target.value = '';
    };

    const handleUpload = async () => {
        if (documents.length === 0) return;
        try {
            await uploadAttachments.mutateAsync({ id: proposal.id, files: documents, orgId });
            toast.success(`${documents.length} file(s) uploaded successfully`);
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to upload files');
        }
    };

    return createPortal(
        <div className='fixed inset-0 z-[9999] flex items-center justify-center' onClick={onClose}>
            <div className='absolute inset-0 bg-black/60 backdrop-blur-sm' />
            <div
                className='relative z-10 bg-bg-dark rounded-xl shadow-2xl w-[90vw] max-w-[520px] flex flex-col'
                onClick={e => e.stopPropagation()}
            >
                <div className='flex items-center justify-between px-20 py-15 border-b border-border'>
                    <div>
                        <h3 className='font-semibold'>Upload Documents</h3>
                        <p className='text-xs text-text-secondary mt-2 truncate max-w-[380px]'>{proposal.title}</p>
                    </div>
                    <button onClick={onClose} className='p-6 rounded-lg hover:bg-danger/10 text-text-secondary hover:text-danger transition-colors'>
                        <IconX size={16} />
                    </button>
                </div>

                <div className='p-20 flex flex-col gap-15'>
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className='border-2 border-dashed border-border rounded-lg p-20 flex flex-col items-center justify-center gap-10 cursor-pointer hover:border-primary transition-colors group'
                    >
                        <IconUpload size={24} className='text-text-secondary group-hover:text-primary transition-colors' />
                        <p className='text-sm text-text-secondary group-hover:text-primary transition-colors'>
                            Click to select files (PDF, images, docs)
                        </p>
                    </div>
                    <input ref={fileInputRef} type='file' multiple
                        accept='.pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.xls,.ppt,.pptx,.csv,.txt,.zip,.rar'
                        onChange={handleFileChange} className='hidden' />

                    {documents.length > 0 && (
                        <ul className='flex flex-col gap-8 max-h-[200px] overflow-y-auto'>
                            {documents.map((doc, i) => (
                                <li key={i} className='flex items-center justify-between gap-10 bg-bg-light border border-border rounded-lg px-15 py-10'>
                                    <div className='flex items-center gap-10 min-w-0'>
                                        {doc.type.startsWith('image/')
                                            ? <IconPhoto size={14} className='text-primary flex-shrink-0' />
                                            : <IconFileText size={14} className='text-primary flex-shrink-0' />
                                        }
                                        <span className='text-sm truncate'>{doc.name}</span>
                                    </div>
                                    <button type='button' onClick={() => setDocuments(prev => prev.filter((_, idx) => idx !== i))}
                                        className='p-5 rounded hover:bg-danger/10 text-text-secondary hover:text-danger transition-colors flex-shrink-0'>
                                        <IconX size={13} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className='flex gap-10 justify-end px-20 py-15 border-t border-border'>
                    <button onClick={onClose} className='btn btn-outline-secondary'>Cancel</button>
                    <button
                        onClick={handleUpload}
                        disabled={documents.length === 0 || uploadAttachments.isPending}
                        className='btn btn-primary'
                    >
                        {uploadAttachments.isPending ? 'Uploading...' : `Upload${documents.length > 0 ? ` (${documents.length})` : ''}`}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

// ── Shared table ──────────────────────────────────────────────────────────

function GrantTable({ data = [], isLoading, onDelete, onUpload, extraActions, showEdit = false }) {
    const navigate = useNavigate();
    const [filterText, setFilterText] = useState('');

    const filtered = data.filter(row =>
        row.title?.toLowerCase().includes(filterText.toLowerCase()) ||
        row.client_donor?.toLowerCase().includes(filterText.toLowerCase())
    );

    const columns = [
        { name: 'Title',              selector: row => row.title,        sortable: true, minWidth: '220px' },
        { name: 'Donor / Organization', selector: row => row.client_donor, sortable: true, minWidth: '180px' },
        {
            name: 'Amount',
            selector: row => row.funding_amount,
            sortable: true,
            width: '140px',
            cell: row => row.funding_amount ? `${row.currency || ''} ${Number(row.funding_amount).toLocaleString()}` : '—',
        },
        { name: 'Lead Person', selector: row => row.lead_person, sortable: true, width: '150px' },
        { name: 'Deadline',    selector: row => row.deadline,    sortable: true, width: '130px' },
        {
            name: 'Actions',
            width: '80px',
            center: true,
            cell: row => (
                <ActionDropdown
                    menuItems={[
                        { label: 'View',   icon: IconEye,   onClick: () => navigate(`/project-management/grant-proposal/${row.id}`) },
                        ...(showEdit ? [{ label: 'Edit', icon: IconEdit, onClick: () => navigate(`/project-management/grant-proposal/${row.id}/edit`) }] : []),
                        ...(onUpload ? [{ label: 'Add Documents', icon: IconUpload, onClick: () => onUpload(row) }] : []),
                        ...(extraActions ? extraActions(row) : []),
                        { label: 'Delete', icon: IconTrash, danger: true, onClick: () => onDelete(row.id, row.title) },
                    ]}
                />
            ),
        },
    ];

    const subHeader = useMemo(() => (
        <div className='flex gap-5 w-full justify-end'>
            <div className='relative w-full max-w-[221px]'>
                <input
                    type='text'
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                    placeholder='Search here...'
                    className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none'
                />
            </div>
            <button onClick={() => setFilterText('')} className="btn btn-danger">Clear</button>
        </div>
    ), [filterText]);

    if (isLoading) return <TableLoader />;

    return (
        <ReactDataTable
            columns={columns}
            data={filtered}
            pagination
            subHeader
            subHeaderComponent={subHeader}
            persistTableHead
        />
    );
}

// ── Per-tab components (each owns its data fetching) ──────────────────────

function DraftTab({ orgId, canDraft, canSendForReview, onDelete, onUpload, doTransition }) {
    const { data = [], isLoading } = useGrantDrafts(orgId);

    const extraActions = (row) => {
        if (!canSendForReview) return [];
        const hasDocuments = (row.attachment_count ?? row.attachments?.length ?? 0) > 0;
        return [{
            label: 'Send for Review',
            icon: IconSend,
            disabled: !hasDocuments,
            description: !hasDocuments ? 'Upload a document first' : undefined,
            onClick: () => doTransition(row.id, 'internal_review', 'Proposal sent for review'),
        }];
    };

    return (
        <GrantTable
            data={data}
            isLoading={isLoading}
            onDelete={onDelete}
            onUpload={canDraft ? onUpload : null}
            extraActions={extraActions}
        />
    );
}

function ReviewTab({ orgId, canSubmit, canAward, canReject, canEditReview, onDelete, onUpload, doTransition }) {
    const { data = [], isLoading } = useGrantInternalReview(orgId);
    const extraActions = (row) => [
        ...(canSubmit ? [{ label: 'Mark Submitted', icon: IconCheck, onClick: () => doTransition(row.id, 'submitted',       'Proposal marked as submitted') }] : []),
        ...(canAward  ? [{ label: 'Award',          icon: IconCheck, onClick: () => doTransition(row.id, 'awarded',         'Proposal awarded') }] : []),
        ...(canReject ? [{ label: 'Reject',         icon: IconX,     danger: true, onClick: () => doTransition(row.id, 'rejected', 'Proposal rejected') }] : []),
    ];
    return <GrantTable data={data} isLoading={isLoading} onDelete={onDelete} onUpload={canEditReview ? onUpload : null} extraActions={extraActions} />;
}

function SubmittedTab({ orgId, canAward, canReject, onDelete, doTransition }) {
    const { data = [], isLoading } = useGrantSubmitted(orgId);
    const extraActions = (row) => [
        ...(canAward  ? [{ label: 'Award',  icon: IconCheck, onClick: () => doTransition(row.id, 'awarded',   'Proposal awarded') }] : []),
        ...(canReject ? [{ label: 'Reject', icon: IconX,     danger: true, onClick: () => doTransition(row.id, 'rejected', 'Proposal rejected') }] : []),
    ];
    return <GrantTable data={data} isLoading={isLoading} onDelete={onDelete} extraActions={extraActions} />;
}

function AwardedTab({ orgId, canDraft, onDelete, onUpload }) {
    const { data = [], isLoading } = useGrantAwarded(orgId);
    return (
        <GrantTable
            data={data}
            isLoading={isLoading}
            onDelete={onDelete}
            onUpload={canDraft ? onUpload : null}
        />
    );
}

function RejectedTab({ orgId, canDraft, onDelete, onUpload, doTransition }) {
    const { data = [], isLoading } = useGrantRejected(orgId);
    const navigate = useNavigate();
    const extraActions = (row) => [
        { label: 'Re-draft', icon: IconRefresh, onClick: () => navigate(`/project-management/grant-proposal/${row.id}/redraft`) },
    ];
    return (
        <GrantTable
            data={data}
            isLoading={isLoading}
            onDelete={onDelete}
            onUpload={canDraft ? onUpload : null}
            extraActions={extraActions}
        />
    );
}

// ── Page ──────────────────────────────────────────────────────────────────

export default function GrantProposal() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;
    const isSuperuser = user?.is_superuser;

    const canDraft         = isSuperuser || hasAnyPermission(['rbacapp.gp_draft']);
    const canSendForReview = isSuperuser || hasAnyPermission(['rbacapp.gp_review']);
    const canEditReview    = isSuperuser || hasAnyPermission(['rbacapp.gp_edit_review']);
    const canViewDraft     = isSuperuser || hasAnyPermission(['rbacapp.gp_view_draft', 'rbacapp.gp_draft']);
    const canViewReview    = isSuperuser || hasAnyPermission(['rbacapp.gp_view_review', 'rbacapp.gp_edit_review']);
    const canViewSubmitted = isSuperuser || hasAnyPermission(['rbacapp.gp_view_submitted']);
    const canViewAwarded   = isSuperuser || hasAnyPermission(['rbacapp.gp_view_awarded']);
    const canViewRejected  = isSuperuser || hasAnyPermission(['rbacapp.gp_view_rejected']);
    const canSubmit        = isSuperuser || hasAnyPermission(['rbacapp.gp_submit']);
    const canAward         = isSuperuser || hasAnyPermission(['rbacapp.gp_award']);
    const canReject        = isSuperuser || hasAnyPermission(['rbacapp.gp_reject']);

    const deleteProposal = useDeleteGrantProposal();
    const transition     = useTransitionGrantProposal();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete,    setItemToDelete]    = useState(null);
    const [uploadProposal,  setUploadProposal]  = useState(null);

    const handleDelete = (id, title) => {
        setItemToDelete({ id, title });
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await deleteProposal.mutateAsync({ id: itemToDelete.id, orgId });
            toast.success('Proposal deleted');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to delete proposal');
        } finally {
            setDeleteModalOpen(false);
            setItemToDelete(null);
        }
    };

    const doTransition = (id, toStatus, successMsg) => {
        transition.mutate(
            { id, data: { to_status: toStatus }, orgId },
            {
                onSuccess: () => toast.success(successMsg),
                onError:   (err) => toast.error(err?.response?.data?.detail || 'Action failed'),
            }
        );
    };

    const sharedProps = { orgId, onDelete: handleDelete, doTransition };

    const allTabs = [
        {
            key: 'draft',
            show: canViewDraft,
            label: 'Draft',
            content: <DraftTab {...sharedProps} canDraft={canDraft} canSendForReview={canSendForReview} onUpload={setUploadProposal} />,
        },
        {
            key: 'review',
            show: canViewReview,
            label: 'Internal Review',
            content: <ReviewTab {...sharedProps} canSubmit={canSubmit} canAward={canAward} canReject={canReject} canEditReview={canEditReview} onUpload={setUploadProposal} />,
        },
        {
            key: 'submitted',
            show: canViewSubmitted,
            label: 'Submitted',
            content: <SubmittedTab {...sharedProps} canAward={canAward} canReject={canReject} />,
        },
        {
            key: 'awarded',
            show: canViewAwarded,
            label: 'Awarded',
            content: <AwardedTab {...sharedProps} canDraft={canDraft} onUpload={setUploadProposal} />,
        },
        {
            key: 'rejected',
            show: canViewRejected,
            label: 'Rejected',
            content: <RejectedTab {...sharedProps} canDraft={canDraft} onUpload={setUploadProposal} />,
        },
    ];

    const tabs           = allTabs.filter(t => t.show);
    const activeTabKey   = searchParams.get('tab') || tabs[0]?.key;
    const activeTabIndex = Math.max(0, tabs.findIndex(t => t.key === activeTabKey));
    const handleTabSelect = (index) => setSearchParams({ tab: tabs[index]?.key }, { replace: true });

    return (
        <>
            <Breadcrumb title="Grant & Proposal" path="Project Management / Grant & Proposal" />
            <div className='bg-bg-dark rounded-lg shadow-light p-20'>
                <div className='flex justify-between items-center mb-20'>
                    <h2 className='text-lg font-bold'>Grant & Proposal</h2>
                    {canDraft && (
                        <button
                            onClick={() => navigate('/project-management/grant-proposal/new')}
                            className='btn btn-primary flex items-center gap-5'
                        >
                            <IconPlus size={16} />
                            Submit Proposal
                        </button>
                    )}
                </div>
                <GrantProposalTab tabs={tabs} selectedIndex={activeTabIndex} onSelect={handleTabSelect} />
            </div>

            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => { setDeleteModalOpen(false); setItemToDelete(null); }}
                onConfirm={confirmDelete}
                itemName={itemToDelete?.title}
                isLoading={deleteProposal.isPending}
            />

            {uploadProposal && (
                <UploadDocumentsModal
                    proposal={uploadProposal}
                    onClose={() => setUploadProposal(null)}
                />
            )}
        </>
    );
}
